import { ApiClient, isApiProblemError } from '@zercode/api-client';

import {
  AuthUser,
  authUserSchema,
  LoginCredentials,
  LoginResult,
  mfaChallengeSchema,
  PartnerOption,
  partnerOptionSchema,
  PartnerSelection,
  partnerSelectionSchema,
  SchoolPartnerSelection,
  schoolPartnerSelectionSchema,
  UserOption,
  schoolOptionListSchema,
  userOptionSchema,
  type SchoolOption,
  refreshResponseSchema,
  PublicPartner,
  PublicRoleApplicationForm,
  RoleApplicationSubmission,
  PublicSchool,
  RegisterCredentials,
  RegisterResult,
  publicPartnerListSchema,
  publicRoleApplicationFormListSchema,
  publicSchoolListSchema,
  registerResultSchema,
  ResendVerificationResult,
  resendVerificationResultSchema,
  tokenPairSchema,
  VerifyEmailResult,
  verifyEmailResultSchema,
  ActivationInfo,
  activationInfoSchema,
  ActivationResult,
  activationResultSchema,
  PasswordResetInfo,
  passwordResetInfoSchema,
  PasswordResetResult,
  passwordResetResultSchema,
  usernameAvailabilitySchema,
  loginLinkSessionSchema
} from './schemas';
import { z } from 'zod';
import { readLinkSession, type LinkSession } from './linkSessionStorage';
import { useSessionStore } from './sessionStore';

const ENDPOINTS = {
  login: '/api/v1/auth/login',
  mfa: '/api/v1/auth/mfa',
  refresh: '/api/v1/auth/refresh',
  // LOGIN-LINK (mig 559): single-use, admin-minted sign-in link → access token only.
  redeemLoginLink: '/api/v1/auth/login-link/redeem',
  logout: '/api/v1/auth/logout',
  // SR: anonymous self-registration + email verification (host-tenant, no bearer)
  register: '/api/v1/auth/register',
  verifyEmail: '/api/v1/auth/verify-email',
  resendVerification: '/api/v1/auth/verify-email/resend',
  // TCH-INV: activation of an INVITED (passwordless) account. Separate from verify-email because
  // consuming this link sets the FIRST password — the server refuses each token at the other's
  // endpoint (mig 289 `purpose`).
  activationInfo: '/api/v1/auth/activation',
  activationComplete: '/api/v1/auth/activation/complete',
  // PWD-RESET (mig 446): self-service password reset. The inverse of activation — this token
  // REPLACES a password where that one sets a first one — and separated by the same `purpose`
  // mechanism, so the server refuses each token at the other's endpoint.
  passwordReset: '/api/v1/auth/password-reset',
  passwordResetInfo: '/api/v1/auth/password-reset',
  passwordResetComplete: '/api/v1/auth/password-reset/complete',
  // RSU: the partner companies a visitor can pick while registering. ANONYMOUS and two fields wide
  // (id + name) — see publicPartnerSchema for why it must not become the full company projection.
  publicPartners: '/api/v1/auth/partners',
  // SIGNUP-SCHOOL: schools IN A COUNTRY (id + name) — anonymous, and empty without a country by
  // design; see publicSchoolSchema for why it must not become the full school projection.
  publicSchools: '/api/v1/auth/schools',
  // BEMORE: the roles a visitor may APPLY for, with their information panel and their questions.
  // Anonymous, host-tenant, LIVE FORMS ONLY. Carries no role code and no role type — an anonymous
  // caller gets what it takes to render and submit the form and nothing describing how the tenant is
  // configured, the same cut publicPartners and publicSchools make.
  roleApplicationForms: '/api/v1/auth/role-application-forms',
  // BEMORE: file an application. Creates NO account — approval does that, and mails an activation
  // link. The response is deliberately identical for every outcome (accepted, duplicate address,
  // already pending) so the form cannot be used to test which addresses exist on this tenant.
  roleApplications: '/api/v1/auth/role-applications',
  // Identity sözleşmesi: profil UserEndpoints altında (2026-07-06 düzeltmesi)
  me: '/api/v1/users/me',
  // SCH-independent: self-service partner assignment (no admin perm; server validates type='partner').
  myCompany: '/api/v1/users/me/company',
  assignablePartners: '/api/v1/users/me/assignable-partners',
  // PPT-2 (migs 326/327): the partner choice is PER PROGRAM TYPE, and it supersedes myCompany for
  // students. myCompany stays because that column is overloaded — it is also the partner EMPLOYEE's
  // employment link, written by the RSU approval flow. Do not repoint it here.
  myPartnerSelections: '/api/v1/users/me/partner-selections',
  // PPT-SCH (mig 329): what the caller's own SCHOOL routes each program type to — the source of the
  // "this came from your school" hint. Its own endpoint because a student holds no schools.view.
  mySchoolPartnerSelections: '/api/v1/users/me/school-partner-selections',
  // SP: self-service salesperson assignment (no admin perm; any live user).
  mySalesperson: '/api/v1/users/me/salesperson',
  salespersonOptions: '/api/v1/users/me/salesperson-options',
  // STU-TCH: self-service teacher assignment. Unlike salesperson (ANY live user), the server requires
  // a live user holding the 'teacher' role — teacher_user_id decides who may see this student's
  // travel / passport-presence / registration data, so it is not a free reference.
  myTeacher: '/api/v1/users/me/teacher',
  teacherOptions: '/api/v1/users/me/teacher-options',
  // SELF-SCHOOL / SELF-TEENEAGLE-PARENT: previously admin-only, now self-service. School must be a live
  // identity.schools row; the parent may be ANY live user but the caller (there is no 'parent' role, and
  // the field is a declaration — the real guardian edge is parent_student_links, admin-managed).
  mySchool: '/api/v1/users/me/school',
  schoolOptions: '/api/v1/users/me/school-options',
  myTeeneagleParent: '/api/v1/users/me/teeneagle-parent',
  // Guardian contact has its OWN endpoint even though /me/profile also carries the two columns: in the
  // Account Center these fields now sit in a different TAB from the demographics form, and that PUT is
  // null-through — one form split across two tabs is how teacherUserId got wiped twice.
  myGuardianContact: '/api/v1/users/me/guardian-contact',
  // AVATAR-UPLOAD: one path, three verbs — POST uploads, DELETE removes, GET streams the bytes.
  myAvatar: '/api/v1/users/me/avatar',
  // USR-NAME (mig 310): self-service handle + the as-you-type availability probe. The probe is
  // AUTHENTICATED on purpose — it answers "does this handle exist", so anonymous access would make it an
  // account-enumeration oracle (which is also why sign-up has no such check).
  myUsername: '/api/v1/users/me/username',
  usernameAvailable: '/api/v1/users/me/username-available',
  // STU-HANDLE (mig 314): the caller's own address. Not part of PUT /users/me — an address change is a
  // security event (it drops email_verified and mails a link), so it is its own endpoint with its own 409.
  myEmail: '/api/v1/users/me/email',
  teeneagleParentOptions: '/api/v1/users/me/teeneagle-parent-options'
} as const;

export interface AuthApi {
  /** Token çifti → authenticated; challenge → mfa (kod ekranı) (P1-3c-2). */
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  /** MFA'nın 2. adımı: challenge + kod → oturum. */
  completeMfa: (challengeToken: string, code: string) => Promise<AuthUser>;
  /** 401 refresh flow — safe to plug straight into ApiClientOptions.onUnauthorized */
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
  me: () => Promise<AuthUser>;
  /** SR: anonymous self-registration → sends a verification email (enumeration-safe).. */
  register: (body: RegisterCredentials) => Promise<RegisterResult>;
  /** RSU: partner companies for the signup pickers (anonymous; no bearer). */
  listPublicPartners: () => Promise<PublicPartner[]>;
  /** BEMORE: live applicant forms for this tenant. Empty array = the tenant offers no Be More door. */
  listRoleApplicationForms: () => Promise<PublicRoleApplicationForm[]>;
  /**
   * BEMORE: submit an application.
   *
   * ⚠ ALWAYS RESOLVES THE SAME WAY when the server accepts the request — including for an address
   * that already has an account, which is filed and refused later at review. Do not try to infer
   * anything about the applicant from the result; a 422 means the ANSWERS are wrong, nothing more.
   */
  submitRoleApplication: (input: RoleApplicationSubmission) => Promise<void>;
  /**
   * SIGNUP-SCHOOL: schools in `countryDefinitionId` for the student signup picker (anonymous).
   * ⚠ The country is REQUIRED in effect — the server answers EMPTY without one rather than listing
   * every institution the tenant works with, so never call this before a country is chosen.
   */
  /**
   * SIGNUP-SEARCH: the schools a visitor may pick, narrowed by `search`.
   *
   * ⚠ THE SERVER RETURNS AT MOST `PUBLIC_SCHOOL_LIST_CAP` ROWS, searched or not. A tenant holding an
   * imported national register has ~12,000 active schools in one country; this is a search box that
   * happens to show a starting page, not a list that happens to be filterable.
   */
  listPublicSchools: (countryDefinitionId: string, search?: string) => Promise<PublicSchool[]>;
  /**
   * SR: confirm the address with the token from the verification email.
   *
   * LINK-SIGNIN: when the server also opened a session, the tokens are stored and `/users/me` fetched
   * before this resolves — so `user` present means "already signed in, route them out". Absent means
   * verified but not signed in (MFA user, tenant at its session limit, or the profile fetch failed),
   * which is a NORMAL outcome the page must handle by showing its sign-in button.
   */
  verifyEmail: (token: string) => Promise<VerifyEmailResult & { user?: AuthUser }>;
  /** SR: re-send the verification email for an address (enumeration-safe). */
  resendVerification: (email: string) => Promise<ResendVerificationResult>;
  /** TCH-INV: describe an activation link so the page can greet the invitee (valid=false if unusable). */
  activationInfo: (token: string) => Promise<ActivationInfo>;
  /**
   * TCH-INV: consume an activation link, setting the invitee's OWN first password.
   * LINK-SIGNIN: resolves with `user` when the link also signed them in — see {@link verifyEmail}.
   */
  completeActivation: (token: string, password: string) => Promise<ActivationResult & { user?: AuthUser }>;
  /**
   * PWD-RESET: ask for a reset link. ALWAYS resolves with the same status — a rejected promise means
   * the request failed (network, 422 on a malformed address, rate limit), never "no such account".
   */
  requestPasswordReset: (email: string) => Promise<PasswordResetResult>;
  /** PWD-RESET: describe a reset link so the page can refuse a dead one (valid=false if unusable). */
  passwordResetInfo: (token: string) => Promise<PasswordResetInfo>;
  /** PWD-RESET: consume a reset link, replacing the password and signing the account out everywhere. */
  completePasswordReset: (token: string, password: string) => Promise<PasswordResetResult>;
  /**
   * LOGIN-LINK: redeems a single-use sign-in link and REPLACES whatever session this browser held
   * (the server revokes the cookie's family and clears the cookie). Resolves with the user and the
   * link session the caller hands to the persona SPA (assignWithLinkSession).
   */
  redeemLoginLink: (token: string) => Promise<{ user: AuthUser; session: LinkSession }>;
  /** SCH-independent: set the current user's own partner company (null clears it). Server validates
   *  the company is a live type='partner' (404 / 422 otherwise). Self-gated (no admin perm). */
  setMyPartner: (companyId: string | null) => Promise<void>;
  /** SCH-independent: the live partner companies the current user may assign to themselves. */
  getAssignablePartners: () => Promise<PartnerOption[]>;
  /** PPT-2: the caller's partner choice for every program type they have made one for. */
  getMyPartnerSelections: () => Promise<PartnerSelection[]>;
  /** PPT-2: set the caller's partner for ONE program type (null clears it). The server accepts only a
   *  live partner that SUPPORTS that type — 422 otherwise, which is why the picker below is filtered
   *  by the same type rather than showing every partner. */
  setMyPartnerSelection: (programTypeId: string, companyId: string | null) => Promise<void>;
  /** PPT-2: the live partners supporting one program type — the filtered picker options. */
  getAssignablePartnersForType: (programTypeId: string) => Promise<PartnerOption[]>;
  /** PPT-SCH: what the caller's own school routes each program type to (empty when they have none). */
  getMySchoolPartnerSelections: () => Promise<SchoolPartnerSelection[]>;
  /** SP: set the current user's own salesperson (null clears it). Self-gated (no admin perm). */
  setMySalesperson: (userId: string | null) => Promise<void>;
  /** SP: the live users the current user may pick as their salesperson. */
  getSalespersonOptions: () => Promise<UserOption[]>;
  /** STU-TCH: set the current user's own teacher (null clears it). Server requires a live user with
   *  the 'teacher' role — 422 otherwise. Self-gated (no admin perm). */
  setMyTeacher: (userId: string | null) => Promise<void>;
  /** SELF-SCHOOL: the caller's own school (null clears). */
  setMySchool: (schoolId: string | null) => Promise<void>;
  /**
   * SCH-PICK: schools the caller may pick, narrowed by `search` and capped by the server at
   * {@link SCHOOL_OPTION_CAP}. Call it with no term for the first page.
   */
  getSchoolOptions: (search?: string) => Promise<SchoolOption[]>;
  /** SELF-TEENEAGLE-PARENT: a declaration, not an access grant (null clears). */
  setMyTeeneagleParent: (userId: string | null) => Promise<void>;
  /** Guardian e-mail / phone (blank or null clears). Own endpoint — see myGuardianContact. */
  setMyGuardianContact: (contact: { parentGuardianEmail: string | null; parentGuardianPhone: string | null }) => Promise<void>;
  /** USR-NAME: set/change/release the caller's handle. Null or blank RELEASES it (e-mail login stays). */
  setMyUsername: (username: string | null) => Promise<void>;
  /** USR-NAME: is the handle free for the caller? The caller's OWN handle counts as available. */
  checkUsernameAvailability: (username: string) => Promise<{ username: string; available: boolean }>;
  /**
   * STU-HANDLE: set/replace the caller's own address. ⚠ Blank does NOT clear (409 on an address another
   * account owns, 400 on blank or malformed) — the server refuses, because an account with no address has no
   * recovery path. The write drops `emailVerified` and mails a verification link to the NEW address.
   */
  setMyEmail: (email: string) => Promise<void>;
  /** AVATAR-UPLOAD: upload an image (server allows jpg/jpeg/png/webp, ≤10MB). */
  uploadMyAvatar: (file: File) => Promise<void>;
  deleteMyAvatar: () => Promise<void>;
  /** The bytes. An <img src> cannot send Authorization, so callers turn this into an object URL. */
  getMyAvatarBlob: () => Promise<Blob>;
  getTeeneagleParentOptions: () => Promise<UserOption[]>;
  /** STU-TCH: the live 'teacher'-role users the current user may pick as their teacher. */
  getTeacherOptions: () => Promise<UserOption[]>;
}

/**
 * Identity (:5101) auth flow. Envelope unwrapping / ProblemDetails /
 * Correlation-Id / Idempotency-Key all come from @zercode/api-client.
 * Session state lands in the Zustand store (memory-only tokens).
 */
export function createAuthApi(identity: ApiClient): AuthApi {
  let refreshInFlight: Promise<boolean> | null = null;

  async function doRefresh(): Promise<boolean> {
    const { refreshToken, clearSession, setTokens } = useSessionStore.getState();
    // LOGIN-LINK: a link-borne session has no refresh token and must NEVER fall back to the cookie —
    // if this browser signed in as someone else in another tab since, the cookie is THEIRS, and a 401
    // here would quietly turn this tab into that user. Its token ending is the session ending.
    if (readLinkSession()) {
      clearSession();
      return false;
    }
    // FE-0/BE-1: the refresh token now travels in the httpOnly cookie (the auth client is created
    // with `sendCredentials: true`, so the browser auto-attaches it). We no longer early-return when
    // there is no in-memory token — a cold second origin has none, yet the cookie carries the session.
    // An in-memory token is still sent when present (same-origin transition / expand-phase fallback).
    try {
      const { data } = await identity.post<unknown>(ENDPOINTS.refresh, refreshToken ? { refreshToken } : undefined);
      const parsed = refreshResponseSchema.parse(data);
      setTokens(parsed.accessToken, parsed.refreshToken);
      return true;
    } catch {
      clearSession();
      return false;
    }
  }

  function refresh(): Promise<boolean> {
    // single-flight: many parallel 401s trigger exactly one refresh call
    refreshInFlight ??= doRefresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function fetchMe(): Promise<AuthUser> {
    const { data } = await identity.get<unknown>(ENDPOINTS.me);
    return authUserSchema.parse(data);
  }

  /** Token çiftini yerleştirir, profili çeker, oturumu açar (login + completeMfa ortak kuyruğu). */
  async function establishSession(tokens: { accessToken: string; refreshToken?: string }): Promise<AuthUser> {
    useSessionStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
    const user = await fetchMe();
    useSessionStore.getState().setSession(user, tokens.accessToken, tokens.refreshToken);
    return user;
  }

  /**
   * LINK-SIGNIN: turn the token pair a verification / activation response may carry into a live local
   * session. Returns undefined when there was nothing to establish.
   *
   * ⚠ IT SWALLOWS ITS OWN FAILURE, deliberately. The link has already been CONSUMED server-side by
   * the time we get here — it is single-use — so rejecting because the follow-up `/users/me` call
   * failed would turn a successful, unrepeatable verification into an error screen, and the user's
   * only recourse would be to request a link they no longer need. Falling back to "verified, not
   * signed in" costs one login and loses nothing.
   */
  async function signInFromLink(tokens: { accessToken: string; refreshToken?: string } | null | undefined): Promise<AuthUser | undefined> {
    if (!tokens) return undefined;
    try {
      return await establishSession(tokens);
    } catch {
      useSessionStore.getState().clearSession();
      return undefined;
    }
  }

  return {
    async login(credentials): Promise<LoginResult> {
      // Login iki sonuçtan biri: token çifti (MFA yok) veya challenge (MFA var).
      const { data } = await identity.post<unknown>(ENDPOINTS.login, credentials);

      // MFA'lı kullanıcıda gövde challengeToken taşır — kod ekranına yönlen.
      const challenge = mfaChallengeSchema.safeParse(data);
      if (challenge.success) {
        return { kind: 'mfa', challenge: challenge.data };
      }

      const tokens = tokenPairSchema.parse(data);
      const user = await establishSession(tokens);
      return { kind: 'authenticated', user };
    },

    async completeMfa(challengeToken, code): Promise<AuthUser> {
      const { data } = await identity.post<unknown>(ENDPOINTS.mfa, { challengeToken, code });
      const tokens = tokenPairSchema.parse(data);
      return establishSession(tokens);
    },

    refresh,

    async logout() {
      try {
        await identity.post<unknown>(ENDPOINTS.logout);
      } finally {
        useSessionStore.getState().clearSession();
      }
    },

    async me() {
      // The auth client deliberately has no onUnauthorized hook (self-recursion
      // guard), so /users/me does its single 401 → refresh → retry here (FE-B).
      let user: AuthUser;
      try {
        user = await fetchMe();
      } catch (err) {
        if (!isApiProblemError(err) || err.status !== 401 || !(await refresh())) throw err;
        user = await fetchMe();
      }
      const { accessToken, refreshToken } = useSessionStore.getState();
      if (accessToken) useSessionStore.getState().setSession(user, accessToken, refreshToken ?? undefined);
      return user;
    },

    async register(body): Promise<RegisterResult> {
      // Anonymous POST (no bearer) through the same client — envelope/ProblemDetails/
      // Correlation-Id handled by @zercode/api-client. Enumeration-safe on the backend.
      const { data } = await identity.post<unknown>(ENDPOINTS.register, body);
      return registerResultSchema.parse(data);
    },

    async listPublicPartners(): Promise<PublicPartner[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.publicPartners);
      return publicPartnerListSchema.parse(data);
    },

    async listRoleApplicationForms(): Promise<PublicRoleApplicationForm[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.roleApplicationForms);
      return publicRoleApplicationFormListSchema.parse(data);
    },

    async submitRoleApplication(input: RoleApplicationSubmission): Promise<void> {
      // Idempotency-Key rides along automatically like any other write [STD-API-4] — the middleware
      // exempts no path, anonymous included.
      await identity.post<unknown>(ENDPOINTS.roleApplications, input);
    },

    async listPublicSchools(countryDefinitionId, search): Promise<PublicSchool[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.publicSchools, {
        // `search` is omitted when empty rather than sent as '': an empty string is a distinct cache
        // key on the server for an identical answer, and this endpoint is anonymous and cached.
        query: search ? { countryDefinitionId, search } : { countryDefinitionId }
      });
      return publicSchoolListSchema.parse(data);
    },

    async verifyEmail(token): Promise<VerifyEmailResult & { user?: AuthUser }> {
      const { data } = await identity.post<unknown>(ENDPOINTS.verifyEmail, { token });
      const result = verifyEmailResultSchema.parse(data);
      return { ...result, user: await signInFromLink(result.tokens) };
    },

    async resendVerification(email): Promise<ResendVerificationResult> {
      const { data } = await identity.post<unknown>(ENDPOINTS.resendVerification, { email });
      return resendVerificationResultSchema.parse(data);
    },

    async activationInfo(token): Promise<ActivationInfo> {
      // Token travels in the PATH, not a query string: it is a credential for its 24h life, and query
      // strings reach access logs and Referer headers far more readily. encodeURIComponent because the
      // value is Base64Url and must survive as one segment.
      const { data } = await identity.get<unknown>(`${ENDPOINTS.activationInfo}/${encodeURIComponent(token)}`);
      return activationInfoSchema.parse(data);
    },

    async completeActivation(token, password): Promise<ActivationResult & { user?: AuthUser }> {
      const { data } = await identity.post<unknown>(ENDPOINTS.activationComplete, { token, password });
      const result = activationResultSchema.parse(data);
      return { ...result, user: await signInFromLink(result.tokens) };
    },

    async requestPasswordReset(email): Promise<PasswordResetResult> {
      const { data } = await identity.post<unknown>(ENDPOINTS.passwordReset, { email });
      return passwordResetResultSchema.parse(data);
    },

    async passwordResetInfo(token): Promise<PasswordResetInfo> {
      // Token in the PATH for the same reason as the activation lookup — it is a credential (for one
      // hour here), and query strings reach access logs and Referer headers far more readily.
      // encodeURIComponent because the value is Base64Url and must survive as one segment.
      const { data } = await identity.get<unknown>(`${ENDPOINTS.passwordResetInfo}/${encodeURIComponent(token)}`);
      return passwordResetInfoSchema.parse(data);
    },

    async redeemLoginLink(token) {
      const { data } = await identity.post<unknown>(ENDPOINTS.redeemLoginLink, { token });
      const parsed = loginLinkSessionSchema.parse(data);
      // The previous user, if any, is gone server-side; drop them locally too BEFORE seeding the new
      // token — setTokens keeps the old refresh token when given none.
      useSessionStore.getState().clearSession();
      useSessionStore.getState().setTokens(parsed.accessToken);
      const user = await fetchMe();
      useSessionStore.getState().setSession(user, parsed.accessToken);
      return { user, session: { accessToken: parsed.accessToken, expiresAt: parsed.accessTokenExpiresAtUtc } };
    },

    async completePasswordReset(token, password): Promise<PasswordResetResult> {
      const { data } = await identity.post<unknown>(ENDPOINTS.passwordResetComplete, { token, password });
      return passwordResetResultSchema.parse(data);
    },

    async setMyPartner(companyId): Promise<void> {
      // Returns UserSummaryDto on the wire; callers re-read /me for the resolved partnerName, so we
      // don't parse the body here.
      await identity.put<unknown>(ENDPOINTS.myCompany, { companyId });
    },

    async getAssignablePartners(): Promise<PartnerOption[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.assignablePartners);
      return z.array(partnerOptionSchema).parse(data);
    },

    async getMyPartnerSelections(): Promise<PartnerSelection[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.myPartnerSelections);
      return z.array(partnerSelectionSchema).parse(data);
    },

    async setMyPartnerSelection(programTypeId, companyId): Promise<void> {
      // The program type is on the ROUTE — the pair (caller, type) IS the row's identity, so it is not
      // a body field that could disagree with the path.
      await identity.put<unknown>(`${ENDPOINTS.myPartnerSelections}/${programTypeId}`, { companyId });
    },

    async getAssignablePartnersForType(programTypeId): Promise<PartnerOption[]> {
      const { data } = await identity.get<unknown>(`${ENDPOINTS.assignablePartners}/${programTypeId}`);
      return z.array(partnerOptionSchema).parse(data);
    },

    async getMySchoolPartnerSelections(): Promise<SchoolPartnerSelection[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.mySchoolPartnerSelections);
      return z.array(schoolPartnerSelectionSchema).parse(data);
    },

    async setMySalesperson(userId): Promise<void> {
      // Callers re-read /me for the resolved salespersonName, so we don't parse the body here.
      await identity.put<unknown>(ENDPOINTS.mySalesperson, { userId });
    },

    async getSalespersonOptions(): Promise<UserOption[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.salespersonOptions);
      return z.array(userOptionSchema).parse(data);
    },

    async setMyTeacher(userId): Promise<void> {
      // Callers re-read /me for the resolved teacherName, so we don't parse the body here.
      await identity.put<unknown>(ENDPOINTS.myTeacher, { userId });
    },

    async getTeacherOptions(): Promise<UserOption[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.teacherOptions);
      return z.array(userOptionSchema).parse(data);
    },

    async setMySchool(schoolId): Promise<void> {
      // Callers re-read /me for the resolved schoolName, so we don't parse the body here.
      await identity.put<unknown>(ENDPOINTS.mySchool, { schoolId });
    },

    async getSchoolOptions(search): Promise<SchoolOption[]> {
      // GET /schools needs identity.schools.view, which a student does not hold, so this dedicated
      // endpoint returns { id, displayName, cityDefinitionId } and nothing else about the institution.
      const { data } = await identity.get<unknown>(ENDPOINTS.schoolOptions, {
        // ⚠ SEARCHED ON THE SERVER, and it has to be: this used to answer with EVERY live school in the
        // caller's country, which after the MEB import is 11,779 rows pulled into a dropdown on every
        // profile load. Omitted rather than sent as '' — an empty string is a distinct key for an
        // identical answer (same reason `listPublicSchools` gives).
        query: search ? { search } : undefined
      });
      return schoolOptionListSchema.parse(data);
    },

    async setMyTeeneagleParent(userId): Promise<void> {
      await identity.put<unknown>(ENDPOINTS.myTeeneagleParent, { userId });
    },

    async getTeeneagleParentOptions(): Promise<UserOption[]> {
      const { data } = await identity.get<unknown>(ENDPOINTS.teeneagleParentOptions);
      return z.array(userOptionSchema).parse(data);
    },

    async setMyGuardianContact(contact): Promise<void> {
      await identity.put<unknown>(ENDPOINTS.myGuardianContact, contact);
    },

    async setMyUsername(username): Promise<void> {
      // Callers re-read /me for the stored (normalised) handle, so the body is not parsed here.
      await identity.put<unknown>(ENDPOINTS.myUsername, { username });
    },

    async setMyEmail(email): Promise<void> {
      // Callers re-read /me for the stored (lowercased) address AND for the reset emailVerified flag, so the
      // body is not parsed here — same reasoning as setMyUsername.
      await identity.put<unknown>(ENDPOINTS.myEmail, { email });
    },

    async checkUsernameAvailability(username): Promise<{ username: string; available: boolean }> {
      const { data } = await identity.get<unknown>(ENDPOINTS.usernameAvailable, { query: { username } });
      return usernameAvailabilitySchema.parse(data);
    },

    async uploadMyAvatar(file): Promise<void> {
      const body = new FormData();
      body.append('file', file);
      // ⚠ Do NOT set Content-Type: the browser must generate the multipart boundary. The client already
      // skips its JSON header for FormData bodies.
      await identity.post<unknown>(ENDPOINTS.myAvatar, body);
    },

    async deleteMyAvatar(): Promise<void> {
      await identity.delete<unknown>(ENDPOINTS.myAvatar);
    },

    async getMyAvatarBlob(): Promise<Blob> {
      return identity.getBlob(ENDPOINTS.myAvatar);
    }
  };
}
